import { ChangeEvent, FC, MouseEvent, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import {
  DropdownWrapperLarge, DropdownWrapperMedium, InputWrapperLarge, InputWrapperMedium
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import MaskedInput from '../../../components/MaskedInput/MaskedInput';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import { SubTitle } from '../../Classes/ClassForm/ClassForm.styles';
import ImageForm from '../../../components/ImageForm/ImageForm';
import { readOnlyLabelTableText } from '../../../constants/GlobalTexts';
import {
  EditForm, EditFormActions, EditFormFields, FormFieldRow, FormRadioGroupContainer
} from "../../Bases/EditForm.styles";
import { Option } from '../../../services/option/option.types';
import { useUpdateOption } from '../../../services/option/option.service';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { statusOptions } from "../../Bases/EditBase/StatusOptions";

interface EditOptionFormProps {
  option: Option;
  onSuccess: () => void;
}

interface EditOptionFormValues {
  status: string;
  displayDescription: string;
  sequence: string;
  displayInOrderEntry: boolean;
  validOnWeb: boolean;
  toppingSubstitutionNo: string;
  makeSequence: string;
  isModifier: boolean;
  opsProcess: string;
  excludeOptionNo: string;
  toppingTypeCode: string;
  webDisplay: string;
  toppingCategory: string;
  pricingSequence: string;
}

const EditOptionForm: FC<EditOptionFormProps> = (
    {
      option,
      onSuccess
    }
) => {
  const classId = option.classNo;
  const optionId = option.optionNo;
  const {
    mutate: updateOption,
    isLoading: updateOptionIsLoading,
    isError: updateOptionIsError,
    error: updateOptionError
  } = useUpdateOption({ classId, optionId });

  useGenericErrorMessage([
    {
      isError: updateOptionIsError,
      error: updateOptionError
    }
  ]);

  const initialFormValues = useMemo<EditOptionFormValues>(() => ({
    status: option.statusNo?.toString() ?? '',
    displayDescription: option.displayDesc,
    sequence: option.sequenceNo?.toString() ?? '',
    displayInOrderEntry: option.displayInOrderEntry,
    validOnWeb: option.validOnWeb,
    toppingSubstitutionNo: option.toppingSubstitutionNo?.toString() ?? '',
    makeSequence: option.makeSeq?.toString() ?? '',
    isModifier: option.isModifier,
    opsProcess: option.opsProcess === ' ' ? 'space' : option.opsProcess,
    excludeOptionNo: option.excludeOptionNo?.toString() ?? '',
    toppingTypeCode: option.toppingTypeCode,
    webDisplay: option.webDisplay?.toString() ?? '',
    toppingCategory: option.toppingCategory,
    pricingSequence: option.pricingSeq ?? ''
  }), [option]);

  const [formValues, setFormValues] = useState<EditOptionFormValues>(initialFormValues);

  const [formErrors, setFormErrors] = useState({
    displayDescription: '',
    makeSequence: '',
    excludeOptionNo: '',
    sequence: '',
    webDisplay: '',
    pricingSequence: '',
    toppingCategory: '',
    toppingTypeCode: '',
    opsProcess: ''
  });

  const unsavedChangesExist = !isEqual(initialFormValues, formValues);

  const updateFormValues = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value
    });
  }

  const updateFormValuesFromRadioGroup = (
      event: ChangeEvent<HTMLInputElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value.includes('Yes')
    });
  }

  const handleSaveClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    let error = false;
    let newFormErrors = {
      displayDescription: '',
      sequence: '',
      makeSequence: '',
      excludeOptionNo: '',
      webDisplay: '',
      pricingSequence: '',
      toppingCategory: '',
      toppingTypeCode: '',
      opsProcess: ''
    };
    if (formValues.displayDescription?.length > 9) {
      error = true;
      newFormErrors.displayDescription = 'BOH Description must be 9 characters or less';
    }
    if (!formValues.displayDescription || formValues.displayDescription.length < 1) {
      error = true;
      newFormErrors.displayDescription = 'Please enter a BOH Description';
    }
    if (formValues.makeSequence.length < 1) {
      error = true;
      newFormErrors.makeSequence = 'Please enter a Make Sequence';
    }
    if (formValues.makeSequence.length > 2) {
      error = true;
      newFormErrors.makeSequence = 'Make Sequence must be 2 characters or less';
    }
    if (+formValues.excludeOptionNo > 2147483646) {
      error = true;
      newFormErrors.excludeOptionNo = 'Exclude Option Number is too long';
    }
    if ([null, undefined, -1, ''].includes(formValues.sequence)) {
      error = true;
      newFormErrors.sequence = 'Sequence Number is required.';
    }
    if (formValues.sequence?.length > 3) {
      error = true;
      newFormErrors.sequence = 'Sequence Number must be 3 characters or less'
    }
    if (+formValues.webDisplay > 2147483646) {
      error = true;
      newFormErrors.webDisplay = 'Web Display Sequence Number is too long';
    }
    if ([null, undefined, -1, ''].includes(formValues.webDisplay)) {
      error = true;
      newFormErrors.webDisplay = 'Web Display Sequence Number is required.';
    }
    if ([null, undefined, -1, ''].includes(formValues.pricingSequence)) {
      error = true;
      newFormErrors.pricingSequence = 'Price Sequence is required.';
    }
    if ([null, undefined, -1, ''].includes(formValues.toppingCategory)) {
      error = true;
      newFormErrors.toppingCategory = 'Please select a Topping Category.';
    }
    if ([null, undefined, -1, ''].includes(formValues.toppingTypeCode)) {
      error = true;
      newFormErrors.toppingCategory = 'Please select a Topping Type Code.';
    }
    if ([null, undefined, -1, ''].includes(formValues.opsProcess)) {
      error = true;
      newFormErrors.opsProcess = 'Please select a BOH Process.';
    }
    if (formValues.pricingSequence?.toString().length > 3) {
      error = true;
      newFormErrors.pricingSequence = 'Price Sequence must be 3 digits or less';
    }
    if (error) {
      setFormErrors(newFormErrors);
      return;
    }

    updateOption(
        {
          status: +formValues.status,
          displayDescription: formValues.displayDescription,
          sequence: +formValues.sequence,
          toppingSubstitutionNo:
              formValues.toppingSubstitutionNo === 'noSelection' ? null : +formValues.toppingSubstitutionNo,
          makeSequence: +formValues.makeSequence,
          displayInOrderEntry: formValues.displayInOrderEntry,
          validOnWeb: formValues.validOnWeb,
          isModifier: formValues.isModifier,
          opsProcess: formValues.opsProcess === 'space' ? ' ' : formValues.opsProcess,
          excludeOptionNo: +formValues.excludeOptionNo,
          toppingTypeCode: formValues.toppingTypeCode,
          webDisplay: +formValues.webDisplay,
          toppingCategory: formValues.toppingCategory,
          pricingSequence: formValues.pricingSequence
        },
        {
          onSuccess: onSuccess
        }
    );
  };

  return (
      <EditForm>
        <EditFormFields>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Class Number"}
                  id={'classNo'}
                  value={option.classNo.toString()}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input
                  label={"Option Number"}
                  id={'optionNo'}
                  value={option.optionNo.toString()}
                  disabled
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Sequence Number"}
                  id={'sequence'}
                  name={'sequence'}
                  value={formValues.sequence}
                  isAllowed={(values) => +values.value < 2147483648}
                  decimalScale={0}
                  onChange={updateFormValues}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  errorText={formErrors.sequence}
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperLarge>
              <Input
                  label={"Short Description"}
                  id={'description'}
                  value={option.description}
                  disabled
              />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Input
                  label={"Long Description"}
                  id={'longDescription'}
                  value={option.longDescription}
                  disabled
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <DropdownWrapperLarge>
              <Dropdown
                  label={"Status"}
                  id={'status'}
                  name={'status'}
                  options={statusOptions}
                  value={formValues.status}
                  onChange={updateFormValues}
              />
            </DropdownWrapperLarge>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Make Sequence"}
                  id={'makeSequence'}
                  name={'makeSequence'}
                  value={formValues.makeSequence}
                  decimalScale={0}
                  onChange={updateFormValues}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  errorText={formErrors.makeSequence}
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <DropdownWrapperLarge>
              <Dropdown
                  label={"Topping Type Code"}
                  id={'toppingTypeCode'}
                  name={'toppingTypeCode'}
                  options={[
                    { value: '0', label: '0 - N/A' },
                    { value: 'C', label: 'C - Cheese Toppings' },
                    { value: 'P', label: 'P - Preferences' },
                    { value: 'T', label: 'T - Normal Toppings' }
                  ]}
                  value={formValues.toppingTypeCode}
                  onChange={updateFormValues}
              />
            </DropdownWrapperLarge>
            <DropdownWrapperLarge>
              <Dropdown
                  label={"Topping Sub Number"}
                  id={'toppingSubstitutionNo'}
                  name={'toppingSubstitutionNo'}
                  options={
                    classId !== 1
                        ? [
                          { value: 'noSelection', label: 'Select' },
                          { value: '0', label: '0 - N/A' },
                          { value: '1', label: '1 - Meat Substitutions' },
                          { value: '2', label: '2 - Vegetable Substitutions' },
                          { value: '9', label: '9 - No Substitutions Allowed' }
                        ]
                        : [
                          { value: '0', label: '0 - N/A' },
                          { value: '1', label: '1 - Meat Substitutions' },
                          { value: '2', label: '2 - Vegetable Substitutions' },
                          { value: '9', label: '9 - No Substitutions Allowed' }
                        ]
                  }
                  value={formValues.toppingSubstitutionNo || 'noSelection'}
                  onChange={updateFormValues}
                  hidePlaceholder={classId !== 1}
              />
            </DropdownWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Exclude Option Number"}
                  id={'excludeOptionNo'}
                  name={'excludeOptionNo'}
                  value={formValues.excludeOptionNo}
                  decimalScale={0}
                  onChange={updateFormValues}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  errorText={formErrors.excludeOptionNo}
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input
                  label={"Mnemonic"}
                  id={'mnemonic'}
                  value={option.mnemonic}
                  disabled
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Display In Order Entry"}
                  id={'displayInOrderEntry'}
                  options={[
                    { label: 'Yes', id: 'displayInOrderEntryYes' },
                    { label: 'No', id: 'displayInOrderEntryNo' }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={formValues.displayInOrderEntry ? 'displayInOrderEntryYes' : 'displayInOrderEntryNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Valid on web"}
                  id={'validOnWeb'}
                  options={[
                    { label: 'Yes', id: 'validOnWebYes' },
                    { label: 'No', id: 'validOnWebNo' }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={formValues.validOnWeb ? 'validOnWebYes' : 'validOnWebNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Web Display Sequence No."}
                  id={'webDisplay'}
                  name={'webDisplay'}
                  value={formValues.webDisplay}
                  isAllowed={(values) => +values.value < 2147483648}
                  onChange={updateFormValues}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  errorText={formErrors.webDisplay}
              />
            </InputWrapperMedium>
            <InputWrapperLarge>
              <Input
                  label={"BOH Description"}
                  id={'displayDescription'}
                  name={'displayDescription'}
                  value={formValues.displayDescription}
                  onChange={updateFormValues}
                  errorText={formErrors.displayDescription}
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <DropdownWrapperMedium>
              <Dropdown
                  label={"BOH Process"}
                  id={'opsProcess'}
                  name={'opsProcess'}
                  options={[
                    { value: 'space', label: ' ' },
                    { value: 'M', label: 'M' },
                    { value: 'C', label: 'C' }
                  ]}
                  value={formValues.opsProcess}
                  onChange={updateFormValues}
                  errorText={formErrors.opsProcess}
              />
            </DropdownWrapperMedium>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"BOH Modifier"}
                  id={'isModifier'}
                  options={[
                    { label: 'Yes', id: 'isModifierYes' },
                    { label: 'No', id: 'isModifierNo' }
                  ]}
                  selected={formValues.isModifier ? 'isModifierYes' : 'isModifierNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
          </FormFieldRow>
          <FormFieldRow>
            <DropdownWrapperLarge>
              <Dropdown
                  label={"Topping Category"}
                  id={'toppingCategory'}
                  name={'toppingCategory'}
                  options={[
                    { value: 'T', label: 'T - Topping' },
                    { value: 'S', label: 'S - Sauce' },
                    { value: 'F', label: 'F - Finisher' },
                    { value: 'D', label: 'D - Drizzle' }
                  ]}
                  value={formValues.toppingCategory}
                  onChange={updateFormValues}
                  errorText={formErrors.toppingCategory}
              />
            </DropdownWrapperLarge>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Price Sequence"}
                  id={'pricingSequence'}
                  name={'pricingSequence'}
                  value={formValues.pricingSequence}
                  decimalScale={0}
                  allowNegative={false}
                  onChange={updateFormValues}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  errorText={formErrors.pricingSequence}
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <ImageForm
              attribute={'topp'}
              resourceNo={[classId, optionId]}
          />
          <SubTitle>{readOnlyLabelTableText}</SubTitle>
        </EditFormFields>
        <EditFormActions>
          <Button
              label={"Save"}
              onClick={handleSaveClick}
              disabled={!unsavedChangesExist}
              isLoading={updateOptionIsLoading}
          />
        </EditFormActions>
        <UnsavedChangesModal unsavedChangesExist={unsavedChangesExist}/>
      </EditForm>
  );
};

export default EditOptionForm;